<template>
    <v-container fluid class="profile-page">
        <!-- Summary of the current choice, in the same diagonal team-color style as the teams page -->
        <v-card
            class="profile-hero rounded-lg"
            :class="{ 'profile-hero--empty': !team, 'profile-hero--mobile': $vuetify.breakpoint.xs }"
            :style="{ '--team-color': (team && team.color) || '#ff7133' }"
        >
            <template v-if="team">
                <div class="profile-hero__logo">
                    <v-img v-if="team.logo" :src="`${assetsUrl}/${team.logo}`" contain class="profile-hero__logo-img" />
                    <v-icon v-else class="profile-hero__logo-icon orange--text">mdi-account-group</v-icon>
                </div>
                <div class="profile-hero__info">
                    <span class="profile-hero__label custom-orange">Η ομάδα μου</span>
                    <div class="profile-hero__team">{{ team.name }}</div>
                    <div v-if="player" class="profile-hero__player">
                        <span class="thumb thumb--lg">
                            <v-img v-if="player.image" :src="`${assetsUrl}/${player.image}`" contain class="thumb__img" />
                            <v-icon v-else>mdi-account</v-icon>
                        </span>
                        <span v-if="player.jersey !== undefined && player.jersey !== null" class="profile-hero__jersey">#{{ player.jersey }}</span>
                        <span>{{ player.name }} {{ player.surname }}</span>
                    </div>
                    <div v-else class="profile-hero__hint">Δεν έχεις διαλέξει παίκτη</div>
                </div>
                <v-btn
                    class="profile-hero__open custom-orange white--text"
                    depressed
                    :to="{ name: 'Team', params: { id: team.id } }"
                >
                    Δες την ομάδα
                    <v-icon small right>mdi-arrow-right</v-icon>
                </v-btn>
            </template>
            <template v-else>
                <v-icon class="profile-hero__empty-icon">mdi-account-group</v-icon>
                <div>
                    <div class="profile-hero__team">Διάλεξε την ομάδα σου</div>
                    <div class="profile-hero__hint">Θα τη βλέπεις πρώτη στις ομάδες και θα λαμβάνεις ειδοποιήσεις για τους αγώνες της.</div>
                </div>
            </template>
        </v-card>

        <v-card class="profile-section rounded-lg">
            <div class="profile-step">
                <span class="profile-step__number custom-orange">1</span>
                <span class="profile-step__title">Ομάδα</span>
            </div>
            <v-autocomplete
                v-model="team"
                label="Διάλεξε ομάδα"
                color="orange"
                item-color="orange"
                item-text="name"
                :items="teams"
                :loading="teamsLoading"
                return-object
                clearable
                outlined
                hide-details
                prepend-inner-icon="mdi-account-group"
                class="secondary-font"
                @change="pickTeam"
            >
                <template v-slot:selection="data">
                    <span class="thumb mr-2">
                        <v-img v-if="data.item.logo" contain :src="`${assetsUrl}/${data.item.logo}`" class="thumb__img" />
                        <v-icon v-else small class="orange--text">mdi-account-group</v-icon>
                    </span>
                    <span class="font-weight-bold">{{ data.item.name }}</span>
                </template>
                <template v-slot:item="data">
                    <v-list-item-avatar tile class="thumb thumb--list">
                        <v-img v-if="data.item.logo" contain :src="`${assetsUrl}/${data.item.logo}`" class="thumb__img" />
                        <v-icon v-else class="orange--text">mdi-account-group</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content class="text-left secondary-font">
                        <v-list-item-title>{{ data.item.name }}</v-list-item-title>
                    </v-list-item-content>
                </template>
            </v-autocomplete>

            <div class="profile-step mt-6">
                <span class="profile-step__number custom-orange">2</span>
                <span class="profile-step__title">Παίκτης</span>
                <span class="profile-step__optional">προαιρετικό</span>
            </div>
            <v-autocomplete
                v-model="player"
                label="Διάλεξε παίκτη"
                color="orange"
                item-color="orange"
                :item-text="item => `${item.name} ${item.surname}`"
                :items="activePlayers"
                :disabled="!team"
                :hint="team ? '' : 'Διάλεξε πρώτα ομάδα'"
                persistent-hint
                return-object
                clearable
                outlined
                prepend-inner-icon="mdi-account"
                class="secondary-font"
                @change="pickPlayer"
            >
                <template v-slot:selection="data">
                    <span class="thumb mr-2">
                        <v-img v-if="data.item.image" contain :src="`${assetsUrl}/${data.item.image}`" class="thumb__img" />
                        <v-icon v-else small>mdi-account</v-icon>
                    </span>
                    <span class="font-weight-bold">{{ formatPlayer(data.item) }}</span>
                </template>
                <template v-slot:item="data">
                    <v-list-item-avatar tile class="thumb thumb--list">
                        <v-img v-if="data.item.image" contain :src="`${assetsUrl}/${data.item.image}`" class="thumb__img" @error="data.item.image = null" />
                        <v-icon v-else>mdi-account</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content class="text-left secondary-font">
                        <v-list-item-title>{{ formatPlayer(data.item) }}</v-list-item-title>
                    </v-list-item-content>
                </template>
            </v-autocomplete>
        </v-card>

        <v-card v-if="team && pushSupported" class="profile-section rounded-lg">
            <div class="profile-notifications">
                <v-icon color="orange" class="profile-notifications__icon">mdi-bell-ring-outline</v-icon>
                <div class="profile-notifications__text">
                    <div class="profile-step__title">Ειδοποιήσεις</div>
                    <div class="profile-hero__hint">
                        {{ player ? 'Για τους αγώνες της ομάδας και τα στατιστικά του παίκτη' : 'Για τους αγώνες της ομάδας' }}
                    </div>
                </div>
                <v-switch
                    :input-value="$store.getters.getNotifications"
                    :loading="notificationsLoading"
                    :disabled="notificationsLoading"
                    color="orange"
                    inset
                    hide-details
                    class="mt-0 pt-0"
                    @change="toggleNotifications"
                />
            </div>
            <div v-if="notificationsError" class="red--text text-caption mt-2">{{ notificationsError }}</div>
        </v-card>

        <div v-if="team" class="profile-actions">
            <v-btn depressed color="white" class="profile-actions__clear red--text" @click="clearChoices()">
                <v-icon small left>mdi-close</v-icon>
                Καθαρισμός επιλογών
            </v-btn>
        </div>
    </v-container>
</template>

<script>
    import teamService from '@/services/team'
    import pushService from '@/services/push'
    export default {
        data() {
            return {
                players: [],
                teams: [],
                team: null,
                player: null,
                teamsLoading: false,
                assetsUrl: 'https://storage.googleapis.com',
                pushSupported: pushService.isSupported(),
                notificationsLoading: false,
                notificationsError: null
            }
        },
        computed: {
            activePlayers() {
                return (this.players || []).filter(player => player.active)
            }
        },
        created() {
            if (this.$store.getters.getTeam) {
                this.fetchTeam(this.$store.getters.getTeam.id)
            }
            this.player = this.$store.getters.getPlayer
            this.fetchAllTeams()
        },
        methods: {
            formatPlayer(player) {
                const jersey = player.jersey !== undefined && player.jersey !== null ? `#${player.jersey} ` : ''
                return `${jersey}${player.name} ${player.surname}`
            },
            pickTeam(team) {
                this.players = team?.players || []
                this.pickPlayer(null)
                this.player = null
                this.$store.dispatch('setTeam', team)
            },
            pickPlayer(player) {
                this.$store.dispatch('setPlayer', player)
            },
            toggleNotifications(enabled) {
                this.notificationsLoading = true
                this.notificationsError = null
                this.$store.dispatch('setNotifications', !!enabled).catch(error => {
                    console.error('Could not update push notifications', error)
                    this.notificationsError = error.message === 'permission-denied' || pushService.isDenied()
                        ? 'Οι ειδοποιήσεις είναι μπλοκαρισμένες. Επιτρέψτε τις από τις ρυθμίσεις του browser.'
                        : 'Δεν ήταν δυνατή η ενεργοποίηση των ειδοποιήσεων.'
                }).finally(() => {
                    this.notificationsLoading = false
                })
            },
            clearChoices() {
                this.team = null
                this.player = null
                this.pickTeam(null)
            },
            fetchTeam(id) {
                teamService.getTeam(id).then(resp => {
                    this.team = resp.data
                    this.players = this.team.players || []
                    this.$store.dispatch('setTeam', this.team)
                    const player = this.player && this.players.find(player => player.id === this.player.id)
                    if (player && player.leader !== this.player.leader) {
                        this.player = player
                        this.$store.dispatch('setPlayer', player)
                    }
                })
            },
            fetchAllTeams() {
                this.teamsLoading = true
                teamService.getTeams(1, 1000).then(resp => {
                    this.teams = resp.data.content
                        .filter(team => team.name !== 'PLAYER POOL')
                        .sort((a, b) => a.name.localeCompare(b.name, 'el'))
                }).finally(() => {
                    this.teamsLoading = false
                })
            }
        }
    }
</script>

<style scoped>
.profile-page {
    max-width: 820px;
    margin: 0 auto;
}

/* ---------- Hero ---------- */
.profile-hero {
    display: flex;
    align-items: center;
    gap: 20px;
    padding: 20px 24px;
    margin-bottom: 16px;
    background: linear-gradient(110deg, var(--team-color) 22%, #fff 22%);
    overflow: hidden;
}

.profile-hero__logo {
    flex: 0 0 110px;
    width: 110px;
    height: 110px;
    padding: 10px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
}

.profile-hero__logo-img {
    width: 100%;
    height: 100%;
}

.profile-hero__logo-icon {
    font-size: 64px !important;
}

.profile-hero__info {
    flex: 1 1 auto;
    min-width: 0;
    text-align: left;
}

.profile-hero__label {
    display: inline-block;
    padding: 1px 8px;
    border-radius: 4px;
    color: #fff;
    font-size: 0.7rem;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.profile-hero__team {
    margin-top: 4px;
    font-size: 1.6rem;
    font-weight: 800;
    line-height: 1.2;
    word-break: break-word;
}

.profile-hero__player {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 10px;
    font-family: Ubuntu, Arial, sans-serif;
    font-weight: 700;
}

.thumb {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 2px;
    border-radius: 6px;
    background: #fff;
    border: 1px solid rgba(0, 0, 0, 0.1);
    overflow: hidden;
}

.thumb--lg {
    width: 42px;
    height: 42px;
}

.thumb--list {
    width: 40px !important;
    min-width: 40px !important;
    height: 40px !important;
}

.thumb__img {
    width: 100%;
    height: 100%;
}

.profile-hero__jersey {
    padding: 0 6px;
    border-radius: 4px;
    background: #0d0d0f;
    color: #fff;
    font-size: 0.8rem;
}

.profile-hero__hint {
    margin-top: 4px;
    color: rgba(0, 0, 0, 0.55);
    font-family: Ubuntu, Arial, sans-serif;
    font-size: 0.9rem;
    font-weight: 500;
}

.profile-hero__open {
    flex: 0 0 auto;
    align-self: flex-end;
    font-family: Ubuntu, Arial, sans-serif;
    font-weight: 700;
    text-transform: none;
    letter-spacing: normal;
}

.profile-hero--empty {
    background: #fff;
    border: 2px dashed rgba(255, 113, 51, 0.6);
}

.profile-hero__empty-icon {
    font-size: 64px !important;
    color: #ff7133 !important;
}

.profile-hero--mobile {
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 20px 16px;
    text-align: center;
    background: linear-gradient(180deg, var(--team-color) 30%, #fff 30%);
}

.profile-hero--mobile.profile-hero--empty {
    background: #fff;
}

.profile-hero--mobile .profile-hero__logo {
    flex-basis: 96px;
    width: 96px;
    height: 96px;
}

.profile-hero--mobile .profile-hero__team {
    font-size: 1.3rem;
}

.profile-hero--mobile .profile-hero__info {
    text-align: center;
}

.profile-hero--mobile .profile-hero__player {
    justify-content: center;
}

.profile-hero--mobile .profile-hero__open {
    align-self: stretch;
}

/* ---------- Sections ---------- */
.profile-section {
    padding: 20px 24px;
    margin-bottom: 16px;
    text-align: left;
}

.profile-step {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
}

.profile-step__number {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    color: #fff;
    font-family: Ubuntu, Arial, sans-serif;
    font-size: 0.85rem;
    font-weight: 800;
}

.profile-step__title {
    font-size: 1.15rem;
    font-weight: 800;
}

.profile-step__optional {
    color: rgba(0, 0, 0, 0.45);
    font-family: Ubuntu, Arial, sans-serif;
    font-size: 0.8rem;
    font-weight: 600;
}

.profile-notifications {
    display: flex;
    align-items: center;
    gap: 14px;
}

.profile-notifications__icon {
    font-size: 30px !important;
}

.profile-notifications__text {
    flex: 1 1 auto;
    text-align: left;
}

/* ---------- Actions ---------- */
.profile-actions {
    display: flex;
    justify-content: center;
}

.profile-actions__clear {
    border: 1px solid rgba(198, 40, 40, 0.4) !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
    font-family: Ubuntu, Arial, sans-serif;
    font-weight: 700;
    text-transform: none;
    letter-spacing: normal;
}

@media (max-width: 600px) {
    .profile-section {
        padding: 16px;
    }
}
</style>
